import { getApiService } from "@/services/apiService";
import { LocationState, ProtectedIsAuthenticatedRouteProps } from "@/types";
import { LoginResponse } from "@/types/auth";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../zustand/Zustand";
import LoadingScreen from "./LoadingScreen";

const ProtectedIsAuthenticatedRoute: React.FC<ProtectedIsAuthenticatedRouteProps> = ({
  children,
  redirectTo = "/login",
  fallback: FallbackComponent
}) => {
  const { isAuthenticated, tokens, setIsAuthenticated, setUserData, setTokens } = useAuth();
  const location = useLocation();
  const api = getApiService();
  const [isCheckingRememberMe, setIsCheckingRememberMe] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only attempt remember-me if:
    // - Not already authenticated
    // - No tokens present (AuthHandler handles token-based flows)
    // - Haven't already checked in this mount
    if (isAuthenticated || tokens?.accessToken || tokens?.refreshToken || hasCheckedRef.current) {
      return;
    }

    hasCheckedRef.current = true;
    setIsCheckingRememberMe(true);

    const checkRememberMe = async () => {
      try {
        const response = await api.post(`/shared/users/login-remember-me`);

        if (response.ok) {
          const data: LoginResponse = await response.json();

          setTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            tokenId: data.tokenId
          });

          setIsAuthenticated(true);
          setUserData(data.user);
        }
      } catch {
        // Silent fail — will fall through to redirect
      } finally {
        setIsCheckingRememberMe(false);
      }
    };

    checkRememberMe();
  }, [isAuthenticated, tokens, api, setTokens, setIsAuthenticated, setUserData]);

  // Show loading while checking remember-me cookie
  if (isCheckingRememberMe) {
    return <LoadingScreen />;
  }

  // If user is not authenticated
  if (!isAuthenticated) {
    // Render fallback component if provided
    if (FallbackComponent) {
      return <FallbackComponent />;
    }

    // Otherwise redirect to login page with current location state
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location } as LocationState}
        replace
      />
    );
  }

  // If user is authenticated, render the child component
  return <>{children}</>;
};

export default ProtectedIsAuthenticatedRoute;